import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { prop } from 'ramda'
import { Button } from 'components/patterns'
import strings from 'utils/strings'
import { createSystemNotification } from 'actions/application'
import { useDispatch, useSelector } from 'react-redux'
import { NOTIFICATION_ERROR, NOTIFICATION_SUCCESS } from 'utils/constants'
import { getRemovedSwitches, getAddedSwitches } from 'utils/feature'
import { requestVehicleFeatures } from 'actions/truck'
import { getTruckFeatures } from 'selectors/truck'
import { postUpdates, getSwitchesAlreadyOnVehicle } from '../utils'
import CommitView from '../CommitView'
import { ContentContainer, Footer } from './FeatureCollections.style'

const Commit = ({
  reset,
  serialNumber,
  exit,
  onGoingSet,
  mappedIoSwitches,
  keepOrRemoveSwitches,
  changeStep,
}) => {
  const dispatch = useDispatch()
  const vehicleFeatures = useSelector((state) =>
    getTruckFeatures(state, { serialNumber })
  )

  const panelNumbersOnVehicle = useMemo(() => {
    const panels = new Set()
    ;(vehicleFeatures || []).forEach((feature) => {
      ;(feature.ioSwitches || []).forEach((io) => {
        ;(io.switches || []).forEach((sw) => {
          if (sw.isSwitchOnVehicle && sw.panelNumber) {
            panels.add(sw.panelNumber)
          }
        })
      })
    })
    return panels
  }, [vehicleFeatures])

  const [commitMessage, setOnCommitMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const added = onGoingSet.filter(prop('isMarkedForAddition'))
  const removed = onGoingSet.filter(prop('isMarkedForDeletion'))

  const handleCancel = () => {
    changeStep(0)
    reset()
  }

  const handleSave = async () => {
    setIsLoading(true)

    const allCandidateSwitches = getAddedSwitches(added, mappedIoSwitches)
    const candidatePanels = allCandidateSwitches
      .map((sw) => sw.panelNumber)
      .filter(Boolean)
    const validationResult = await getSwitchesAlreadyOnVehicle(
      dispatch,
      serialNumber,
      candidatePanels
    )

    if (!validationResult.ok) {
      dispatch(
        createSystemNotification({
          notificationMessage: strings.switch_validation_failed,
          notificationType: NOTIFICATION_ERROR,
          persistent: true,
          closable: true,
        })
      )
      setIsLoading(false)
      return
    }

    const allExcluded = new Set(panelNumbersOnVehicle)
    validationResult.panels.forEach((p) => allExcluded.add(p))

    const addedSwitches = allCandidateSwitches.filter(
      (sw) => !allExcluded.has(sw.panelNumber)
    )
    const removedSwitches = getRemovedSwitches(removed, keepOrRemoveSwitches)

    const prepSwitchesForPost = (switches = []) =>
      switches.reduce(
        (acc, curr) => ({ ...acc, [curr.panelNumber]: curr.panelNumber }),
        {}
      )

    try {
      await postUpdates(
        dispatch,
        serialNumber,
        onGoingSet,
        commitMessage,
        prepSwitchesForPost(addedSwitches),
        prepSwitchesForPost(removedSwitches)
      )

      dispatch(requestVehicleFeatures(serialNumber))

      dispatch(
        createSystemNotification({
          notificationMessage: strings.vehicle_updated,
          notificationType: NOTIFICATION_SUCCESS,
          persistent: true,
          closable: true,
        })
      )

      exit()
    } catch {
      // The API error handler chain already shows the appropriate error modal
      // (via defaultErrorHandler or a domain-specific handler). We only need
      // to clear the loading state so the user can retry.
    } finally {
      setIsLoading(false)
    }
  }

  const disableCommit = !commitMessage.length || isLoading

  return (
    <>
      <ContentContainer>
        <CommitView
          serialNumber={serialNumber}
          updated={[]}
          added={added}
          removed={removed}
          commitMessage={commitMessage}
          onCommitMessageChange={(e) => setOnCommitMessage(e.target.value)}
          mappedIoSwitches={mappedIoSwitches}
          keepOrRemoveSwitches={keepOrRemoveSwitches}
        />
      </ContentContainer>

      <Footer>
        <Button text={strings.cancel} type="secondary" onClick={handleCancel} />
        <Button
          text={strings.commit_to_vehicle}
          onClick={handleSave}
          disabled={disableCommit}
          loading={isLoading}
        />
      </Footer>
    </>
  )
}

Commit.propTypes = {
  serialNumber: PropTypes.string,
  changeStep: PropTypes.func,
  exit: PropTypes.func,
  reset: PropTypes.func,
  onGoingSet: PropTypes.array,
  mappedIoSwitches: PropTypes.object,
  keepOrRemoveSwitches: PropTypes.array,
}

export default Commit
